import { MessageKey, t } from "../i18n";
import { showActionModal } from "./modal";
import { track } from "../telemetry";

/**
 * Live accessors for the real UI elements a tutorial step points at. Palette
 * buttons are resolved lazily because the palette is rebuilt on feature
 * changes, so a cached element would go stale; the panel / toggle buttons are
 * resident and handed over directly.
 */
export interface TutorialTargets {
  sequencerBtn: () => HTMLElement | null;
  boxBtn: () => HTMLElement | null;
  scenePanelBtn: HTMLElement;
  sceneSettingsNavBtn: HTMLElement;
  featuresNavBtn: HTMLElement;
  communityNavBtn: () => HTMLElement | null;
  touchPlayBtn: HTMLElement;
  timbreRow: () => HTMLElement | null;
  radial: {
    active: () => boolean;
    isOpen: () => boolean;
    itemRect: (key: string) => DOMRect | null;
    backRect: () => DOMRect | null;
    open: (clientX: number, clientY: number) => void;
    selectItem: (key: string) => void;
    selectBack: () => void;
    openScenePanelSection: (sectionTitle: string) => void;
  };
}

/**
 * Side-channel into the running app. `onBegin` / `onEnd` let the tutorial own
 * the screen (suppress hover panels, set a sensible volume) for its duration,
 * and `entityCount` lets a placement step confirm a drag actually created an
 * object before advancing.
 */
export interface TutorialHooks {
  onBegin: () => void;
  onEnd: () => void;
  setDeleteLock: (locked: boolean) => void;
  ensurePlacementOn: () => void;
  placeForTutorial: (tool: "emitter-sync" | "box") => boolean;
  relocateTutorialBox: () => boolean;
  boxDropRect: () => DOMRect | null;
  selectTutorialBox: () => boolean;
  deleteTutorialBox: () => boolean;
  nudgeTutorialBoxPitch: () => boolean;
  setTutorialBoxTimbre: (timbreId: string) => boolean;
  entityCount: () => number;
  targetBoxRect: () => DOMRect | null;
  boxSelected: () => boolean;
  soundPlayedCount: () => number;
  boxPitch: () => number | null;
  boxTimbre: () => string | null;
}

export interface Tutorial {
  start: () => void;
  shouldAutoShow: () => boolean;
}

/**
 * Subscribes to the operation that advances a step and invokes `advance` once
 * it happens, returning an unsubscribe. Each step detects its own real action
 * (placing an object, clicking a panel, dragging to a delete edge) so regular
 * progression stays tied to concrete interaction.
 */
type StepWatcher = (advance: () => void) => () => void;

interface TutorialStep {
  titleKey: () => MessageKey;
  bodyKey: () => MessageKey;
  hole: () => DOMRect | null;
  watch: StepWatcher;
  perform: () => void;
}

interface TutorialState {
  completed: boolean;
  skipped: boolean;
  dontShowAgain: boolean;
}

const STORAGE_KEY = "resonella.tutorial";
const STATE_VERSION = 1;
const PIANO_TIMBRE = "piano";

function loadState(): TutorialState | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as { version?: number; values?: unknown };
    const values = parsed.values;
    if (typeof values !== "object" || values === null) {
      return null;
    }
    const v = values as Record<string, unknown>;
    return { completed: v.completed === true, skipped: v.skipped === true, dontShowAgain: v.dontShowAgain === true };
  } catch {
    return null;
  }
}

function saveState(state: TutorialState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STATE_VERSION, values: state }));
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

function rectOf(el: HTMLElement | null): DOMRect | null {
  if (el === null) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    return null;
  }
  return rect;
}

/** Bounding rect of an element's rendered text, so a wide button gets a hole sized to its label rather than its full width. */
function textRectOf(el: HTMLElement): DOMRect | null {
  const range = document.createRange();
  range.selectNodeContents(el);
  const rect = range.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    return rectOf(el);
  }
  return rect;
}

const SVG_NS = "http://www.w3.org/2000/svg";

export function setupTutorial(targets: TutorialTargets, hooks: TutorialHooks): Tutorial {
  const overlay = document.createElement("div");
  overlay.className = "tutorial-overlay";

  const maskSvg = document.createElementNS(SVG_NS, "svg");
  maskSvg.classList.add("tutorial-mask");
  const maskPath = document.createElementNS(SVG_NS, "path");
  maskPath.setAttribute("fill-rule", "evenodd");
  maskPath.addEventListener("pointerdown", (ev: PointerEvent): void => ev.stopPropagation());
  const ring = document.createElementNS(SVG_NS, "rect");
  ring.classList.add("tutorial-ring");
  maskSvg.append(maskPath, ring);

  const caption = document.createElement("div");
  caption.className = "tutorial-caption";
  caption.addEventListener("pointerdown", (ev: PointerEvent): void => ev.stopPropagation());
  const captionHeader = document.createElement("div");
  captionHeader.className = "tutorial-caption-header";
  const captionTitle = document.createElement("div");
  captionTitle.className = "tutorial-caption-title";
  const skipBtn = document.createElement("button");
  skipBtn.className = "tutorial-btn tutorial-skip-btn";
  captionHeader.append(captionTitle, skipBtn);
  const captionBody = document.createElement("div");
  captionBody.className = "tutorial-caption-body";
  const doneOpt = document.createElement("label");
  doneOpt.className = "tutorial-done-opt";
  const doneOptInput = document.createElement("input");
  doneOptInput.type = "checkbox";
  const doneOptText = document.createElement("span");
  doneOptText.className = "tutorial-done-opt-text";
  doneOpt.append(doneOptInput, doneOptText);
  const progressRow = document.createElement("div");
  progressRow.className = "tutorial-progress-row";
  const progressEl = document.createElement("div");
  progressEl.className = "tutorial-progress";
  const nextBtn = document.createElement("button");
  nextBtn.className = "tutorial-btn tutorial-next-btn";
  progressRow.append(progressEl, nextBtn);
  caption.append(captionHeader, captionBody, doneOpt, progressRow);

  overlay.append(maskSvg, caption);

  let shown = false;
  let index = 0;
  let pollId = 0;
  let watchCleanup: (() => void) | null = null;
  let boxPhase = 0;
  let boxRadialPlaced = false;
  let advancedPhase = 0;
  let radialAdvancedReached = false;
  let tunePhase = 0;
  let deletePhase = 0;
  let savePhase = 0;

  function visiblePitchButtons(): HTMLElement | null {
    return (
      Array.from(document.querySelectorAll<HTMLElement>(".pitch-buttons")).find(
        (el: HTMLElement): boolean => el.getBoundingClientRect().width > 0,
      ) ?? null
    );
  }

  function clickTarget(target: HTMLElement | null): void {
    if (target !== null) {
      target.click();
    }
  }

  /**
   * Releases the overlay for a palette drag and advances only if the drop
   * actually created an object (entity count grew); a cancelled drag re-arms
   * the same step so the user stays until placement succeeds.
   */
  function watchPlace(getEl: () => HTMLElement | null): StepWatcher {
    return (advance: () => void): (() => void) => {
      let active = true;
      let armedEl: HTMLElement | null = null;
      const before = hooks.entityCount();
      const onDown = (): void => {
        overlay.classList.add("tutorial-hidden");
        window.addEventListener(
          "pointerup",
          (): void => {
            if (!active) {
              return;
            }
            overlay.classList.remove("tutorial-hidden");
            if (hooks.entityCount() > before) {
              advance();
            } else {
              arm();
            }
          },
          { once: true },
        );
      };
      const arm = (): void => {
        const el = getEl();
        if (el === null) {
          return;
        }
        armedEl = el;
        el.addEventListener("pointerdown", onDown, { capture: true, once: true });
      };
      arm();
      const poll = window.setInterval((): void => {
        if (hooks.entityCount() > before) {
          advance();
        }
      }, 150);
      return (): void => {
        active = false;
        window.clearInterval(poll);
        if (armedEl !== null) {
          armedEl.removeEventListener("pointerdown", onDown, { capture: true });
        }
      };
    };
  }

  /**
   * Place an object, then wait until it actually makes sound — a particle must
   * strike a collider (sound-pulse count grows past the post-placement baseline)
   * before advancing, so the step ends on the audible result, not the drop.
   */
  function watchBoxSound(getEl: () => HTMLElement | null): StepWatcher {
    return (advance: () => void): (() => void) => {
      let active = true;
      let armedEl: HTMLElement | null = null;
      let soundBaseline: number | null = null;
      let promoteId = 0;
      const before = hooks.entityCount();
      const enterPostPlace = (): void => {
        if (soundBaseline !== null) {
          return;
        }
        soundBaseline = hooks.soundPlayedCount();
        if (promoteId !== 0) {
          window.clearTimeout(promoteId);
        }
        promoteId = window.setTimeout((): void => {
          if (soundBaseline !== null && hooks.soundPlayedCount() <= soundBaseline) {
            boxPhase = 2;
            overlay.classList.remove("tutorial-hidden");
            repaint();
          }
        }, 500);
      };
      const onDown = (): void => {
        overlay.classList.add("tutorial-hidden");
        window.addEventListener(
          "pointerup",
          (): void => {
            if (!active) {
              return;
            }
            if (hooks.entityCount() > before) {
              enterPostPlace();
            } else {
              overlay.classList.remove("tutorial-hidden");
              arm();
            }
          },
          { once: true },
        );
      };
      const arm = (): void => {
        const el = getEl();
        if (el === null) {
          return;
        }
        armedEl = el;
        el.addEventListener("pointerdown", onDown, { capture: true, once: true });
      };
      arm();
      const poll = window.setInterval((): void => {
        if (soundBaseline === null && hooks.entityCount() > before) {
          enterPostPlace();
          return;
        }
        if (soundBaseline !== null && hooks.soundPlayedCount() > soundBaseline) {
          advance();
          return;
        }
        if (boxPhase >= 2 && hooks.boxSelected()) {
          advance();
        }
      }, 150);
      return (): void => {
        active = false;
        window.clearInterval(poll);
        if (promoteId !== 0) {
          window.clearTimeout(promoteId);
        }
        boxPhase = 0;
        if (armedEl !== null) {
          armedEl.removeEventListener("pointerdown", onDown, { capture: true });
        }
      };
    };
  }

  function watchClick(getEl: () => HTMLElement | null): StepWatcher {
    return (advance: () => void): (() => void) => {
      const el = getEl();
      if (el === null) {
        return (): void => {};
      }
      const onClick = (): void => advance();
      el.addEventListener("click", onClick, { capture: true });
      return (): void => el.removeEventListener("click", onClick, { capture: true });
    };
  }

  /**
   * Guides the user through the scene hover panel into Scene Settings. Hover
   * panels are re-enabled for this step only; the spotlight first sits on the
   * scene button, then jumps to the Scene Settings entry once the panel opens,
   * and advances when that entry is clicked.
   */
  function watchAdvanced(): StepWatcher {
    return (advance: () => void): (() => void) => {
      const featBtn = targets.featuresNavBtn;
      let revealId = 0;
      const onFeatClick = (): void => {
        if (advancedPhase !== 2 || revealId !== 0) {
          return;
        }
        overlay.classList.add("tutorial-hidden");
        revealId = window.setTimeout(advance, 1000);
      };
      featBtn.addEventListener("click", onFeatClick);
      const poll = window.setInterval((): void => {
        if (advancedPhase === 0 && targets.sceneSettingsNavBtn.offsetParent !== null) {
          advancedPhase = 1;
          repaint();
        } else if (advancedPhase === 1 && featBtn.offsetParent !== null) {
          advancedPhase = 2;
          repaint();
        }
      }, 150);
      return (): void => {
        window.clearInterval(poll);
        if (revealId !== 0) {
          window.clearTimeout(revealId);
        }
        featBtn.removeEventListener("click", onFeatClick);
        advancedPhase = 0;
      };
    };
  }

  /**
   * Guides editing the selected box: pick it, change its pitch with the ◀ ▶
   * buttons, then change its timbre. Each phase moves the spotlight (box → pitch
   * overlay → timbre row) and advances on the actual parameter change.
   */
  function watchTune(): StepWatcher {
    return (advance: () => void): (() => void) => {
      let pitchBaseline: number | null = null;
      const poll = window.setInterval((): void => {
        if (tunePhase === 0) {
          if (hooks.boxSelected()) {
            tunePhase = 1;
            pitchBaseline = hooks.boxPitch();
            repaint();
          }
          return;
        }
        if (!hooks.boxSelected()) {
          tunePhase = 0;
          repaint();
          return;
        }
        if (tunePhase === 1) {
          const pitch = hooks.boxPitch();
          if (pitch !== null && pitchBaseline !== null && pitch !== pitchBaseline) {
            tunePhase = 2;
            repaint();
          }
          return;
        }
        if (hooks.boxTimbre() === PIANO_TIMBRE) {
          advance();
        }
      }, 150);
      return (): void => {
        window.clearInterval(poll);
        tunePhase = 0;
      };
    };
  }

  /**
   * Two-stage delete: wait until the box is selected (skipped when it already
   * is, e.g. carried over from the tune step), then make the overlay
   * click-through so it can be dragged out, advancing once a delete actually
   * removes an object (entity count drops below the baseline).
   */
  function watchDelete(): StepWatcher {
    return (advance: () => void): (() => void) => {
      const before = hooks.entityCount();
      let dragging = false;
      const onDragStart = (): void => {
        dragging = true;
        overlay.classList.add("tutorial-hidden");
      };
      const onDragEnd = (): void => {
        dragging = false;
      };
      const enterDragPhase = (): void => {
        deletePhase = 1;
        window.addEventListener("pointerdown", onDragStart);
        window.addEventListener("pointerup", onDragEnd);
      };
      const exitDragPhase = (): void => {
        deletePhase = 0;
        dragging = false;
        overlay.classList.remove("tutorial-hidden");
        window.removeEventListener("pointerdown", onDragStart);
        window.removeEventListener("pointerup", onDragEnd);
      };
      if (hooks.boxSelected()) {
        enterDragPhase();
        repaint();
      }
      const poll = window.setInterval((): void => {
        if (hooks.entityCount() < before) {
          advance();
          return;
        }
        if (deletePhase === 0) {
          if (hooks.boxSelected()) {
            enterDragPhase();
            repaint();
          }
          return;
        }
        if (hooks.entityCount() < before) {
          advance();
          return;
        }
        if (!dragging) {
          if (!hooks.boxSelected()) {
            exitDragPhase();
            repaint();
            return;
          }
          overlay.classList.remove("tutorial-hidden");
        }
      }, 150);
      return (): void => {
        window.clearInterval(poll);
        window.removeEventListener("pointerdown", onDragStart);
        window.removeEventListener("pointerup", onDragEnd);
        overlay.classList.remove("tutorial-hidden");
        deletePhase = 0;
      };
    };
  }

  /**
   * Final step: open the scene panel, then guide to the Community button and
   * end with the Community screen showing. Falls back to a plain scene-panel
   * click when sharing (and thus the Community entry) is unavailable.
   */
  function watchSave(): StepWatcher {
    return (advance: () => void): (() => void) => {
      const community = targets.communityNavBtn();
      if (community === null) {
        return watchClick((): HTMLElement => targets.scenePanelBtn)(advance);
      }
      const onClick = (): void => advance();
      community.addEventListener("click", onClick);
      const poll = window.setInterval((): void => {
        if (savePhase === 0 && community.offsetParent !== null) {
          savePhase = 1;
          repaint();
        }
      }, 150);
      return (): void => {
        window.clearInterval(poll);
        community.removeEventListener("click", onClick);
        savePhase = 0;
      };
    };
  }

  /**
   * Spotlight for a radial-mode placement step: before the menu opens there is
   * nothing to point at (the user taps the background), so the spotlight follows
   * the path — the 生成 sector at root, then the tool itself once drilled in.
   * itemRect returns null when a key is not on the active level, which walks the
   * highlight forward automatically as the user strokes deeper.
   */
  /** A centered square spotlight (40% of the shorter viewport side) prompting the first tap anywhere in it to open the radial. */
  function radialOpenHintHole(): DOMRect {
    const s = Math.min(window.innerWidth, window.innerHeight) * 0.4;
    return new DOMRect((window.innerWidth - s) / 2, (window.innerHeight - s) / 2, s, s);
  }

  /**
   * Radial box step: a place-only advance is wrong (the box must be hit to count),
   * so this waits for placement, then advances only once a particle strikes it
   * (sound-pulse count grows past the post-placement baseline). On placement it
   * flips boxRadialPlaced so the caption/hole switch to the "move it where the
   * particles hit" phase, and — like the delete step — hides the spotlight while
   * the box is being dragged so it never obscures the drop target.
   */
  function watchRadialBoxSound(): StepWatcher {
    return (advance: () => void): (() => void) => {
      const before = hooks.entityCount();
      let soundBaseline: number | null = null;
      boxRadialPlaced = false;
      const onDragStart = (): void => {
        if (boxRadialPlaced) {
          overlay.classList.add("tutorial-hidden");
        }
      };
      const onDragEnd = (): void => overlay.classList.remove("tutorial-hidden");
      window.addEventListener("pointerdown", onDragStart);
      window.addEventListener("pointerup", onDragEnd);
      const poll = window.setInterval((): void => {
        if (soundBaseline === null) {
          if (hooks.entityCount() > before) {
            soundBaseline = hooks.soundPlayedCount();
            boxRadialPlaced = true;
            repaint();
          }
        } else if (hooks.soundPlayedCount() > soundBaseline) {
          advance();
        }
      }, 200);
      return (): void => {
        window.clearInterval(poll);
        window.removeEventListener("pointerdown", onDragStart);
        window.removeEventListener("pointerup", onDragEnd);
        overlay.classList.remove("tutorial-hidden");
        boxRadialPlaced = false;
      };
    };
  }

  function radialPlaceHole(toolKey: string): DOMRect | null {
    if (!targets.radial.isOpen()) {
      return radialOpenHintHole();
    }
    return targets.radial.itemRect(toolKey) ?? targets.radial.itemRect("generate");
  }

  /**
   * Spotlight for a staged Scene-panel flow (open → back-if-drilled → Scene →
   * child): the open hint, then — if the radial reopened drilled into another
   * branch (e.g. Create) — the Back wedge, then the Scene sector, then the child.
   * Each stage's key being absent on the current level walks the highlight on.
   */
  function radialScenePathHole(childKey: string): DOMRect | null {
    if (!targets.radial.isOpen()) {
      return radialOpenHintHole();
    }
    return targets.radial.itemRect(childKey) ?? targets.radial.itemRect("scene") ?? targets.radial.backRect();
  }

  /**
   * One 次へ press = one manual radial tap: opens the menu at the spotlight
   * center, then drills exactly one level per call (Back first when the menu
   * reopened inside another branch), so the assist takes the same number of
   * presses as the guided taps and the user sees every stage. Returns true on
   * the press that confirmed `targetKey`.
   */
  function performRadialStage(parentKey: string, targetKey: string, anchorRect: DOMRect | null = null): boolean {
    if (!targets.radial.isOpen()) {
      const hint = anchorRect ?? radialOpenHintHole();
      targets.radial.open(hint.x + hint.width / 2, hint.y + hint.height / 2);
      return false;
    }
    if (targets.radial.itemRect(targetKey) !== null) {
      targets.radial.selectItem(targetKey);
      return true;
    }
    if (targets.radial.itemRect(parentKey) !== null) {
      targets.radial.selectItem(parentKey);
      return false;
    }
    targets.radial.selectBack();
    return false;
  }

  function watchEntityGrowth(): StepWatcher {
    return (advance: () => void): (() => void) => {
      const before = hooks.entityCount();
      const poll = window.setInterval((): void => {
        if (hooks.entityCount() > before) {
          advance();
        }
      }, 200);
      return (): void => window.clearInterval(poll);
    };
  }


  /**
   * Radial step-6 finisher: advance one second after the Features nav button is
   * tapped (overlay hidden meanwhile), mirroring the desktop watchAdvanced so the
   * user sees the Features sub-section open before the tutorial moves on. Latches
   * radialAdvancedReached once the Features button is visible so the caption never
   * rewinds to the "open Scene Settings" hint after the panel is closed again.
   */
  function watchRadialFeatures(): StepWatcher {
    return (advance: () => void): (() => void) => {
      const btn = targets.featuresNavBtn;
      let revealId = 0;
      const onClick = (): void => {
        if (revealId !== 0) {
          return;
        }
        overlay.classList.add("tutorial-hidden");
        revealId = window.setTimeout(advance, 1000);
      };
      btn.addEventListener("click", onClick);
      const poll = window.setInterval((): void => {
        if (btn.offsetParent !== null) {
          radialAdvancedReached = true;
        }
      }, 150);
      return (): void => {
        window.clearInterval(poll);
        btn.removeEventListener("click", onClick);
        if (revealId !== 0) {
          window.clearTimeout(revealId);
        }
      };
    };
  }

  function watchFeedOpen(): StepWatcher {
    return (advance: () => void): (() => void) => {
      const poll = window.setInterval((): void => {
        if (document.body.classList.contains("feed-open")) {
          advance();
        }
      }, 200);
      return (): void => window.clearInterval(poll);
    };
  }

  /**
   * Radial save step when Community is unavailable (sharing off): the desktop
   * flow keys off the hidden scene-panel button, so instead advance once the
   * Scene Settings panel is actually open (the Features nav becomes visible),
   * which the "次へ" perform opens directly.
   */
  function watchRadialScenePanelOpened(): StepWatcher {
    return (advance: () => void): (() => void) => {
      const poll = window.setInterval((): void => {
        if (targets.featuresNavBtn.offsetParent !== null) {
          advance();
        }
      }, 200);
      return (): void => window.clearInterval(poll);
    };
  }

  const steps: TutorialStep[] = [
    {
      titleKey: (): MessageKey => "tutorial.sequencer.title",
      bodyKey: (): MessageKey => {
        if (!targets.radial.active()) {
          return "tutorial.sequencer.body";
        }
        if (!targets.radial.isOpen()) {
          return "tutorial.sequencer.radial.open";
        }
        return targets.radial.itemRect("generate") !== null ? "tutorial.sequencer.radial.generate" : "tutorial.sequencer.radial.tool";
      },
      hole: (): DOMRect | null => (targets.radial.active() ? radialPlaceHole("emitter-sync") : rectOf(targets.sequencerBtn())),
      watch: (advance: () => void): (() => void) =>
        targets.radial.active() ? watchEntityGrowth()(advance) : watchPlace(targets.sequencerBtn)(advance),
      perform: (): void => {
        if (hooks.entityCount() !== 0) {
          return;
        }
        if (targets.radial.active()) {
          performRadialStage("generate", "emitter-sync");
          return;
        }
        hooks.placeForTutorial("emitter-sync");
      },
    },
    {
      titleKey: (): MessageKey => "tutorial.box.title",
      bodyKey: (): MessageKey => {
        if (!targets.radial.active()) {
          return boxPhase < 2 ? "tutorial.box.body" : "tutorial.box.selectBody";
        }
        if (boxRadialPlaced) {
          return "tutorial.box.radial.contact";
        }
        if (!targets.radial.isOpen()) {
          return "tutorial.box.radial.open";
        }
        return targets.radial.itemRect("generate") !== null ? "tutorial.box.radial.generate" : "tutorial.box.radial.tool";
      },
      hole: (): DOMRect | null => {
        if (!targets.radial.active()) {
          return boxPhase === 0 ? rectOf(targets.boxBtn()) : hooks.targetBoxRect();
        }
        if (boxRadialPlaced) {
          return hooks.targetBoxRect();
        }
        if (!targets.radial.isOpen()) {
          return hooks.boxDropRect() ?? radialOpenHintHole();
        }
        return radialPlaceHole("box");
      },
      watch: (advance: () => void): (() => void) =>
        targets.radial.active() ? watchRadialBoxSound()(advance) : watchBoxSound(targets.boxBtn)(advance),
      perform: (): void => {
        const hasBox = hooks.targetBoxRect() !== null;
        if (targets.radial.active()) {
          if (!hasBox && !boxRadialPlaced) {
            if (performRadialStage("generate", "box", hooks.boxDropRect())) {
              hooks.relocateTutorialBox();
            }
            return;
          }
          if (!hooks.boxSelected()) {
            hooks.selectTutorialBox();
          }
          return;
        }
        if (!hasBox && boxPhase === 0) {
          const placed = hooks.placeForTutorial("box");
          if (placed) {
            boxPhase = 2;
          }
          return;
        }
        if (boxPhase >= 2 && !hooks.boxSelected()) {
          hooks.selectTutorialBox();
          return;
        }
      },
    },
    {
      titleKey: (): MessageKey => "tutorial.tune.title",
      bodyKey: (): MessageKey =>
        tunePhase === 0
          ? "tutorial.tune.selectBody"
          : tunePhase === 1
            ? "tutorial.tune.pitchBody"
            : "tutorial.tune.timbreBody",
      hole: (): DOMRect | null =>
        tunePhase === 0
          ? hooks.targetBoxRect()
          : tunePhase === 1
            ? rectOf(visiblePitchButtons())
            : rectOf(targets.timbreRow()),
      watch: watchTune(),
      perform: (): void => {
        if (tunePhase === 0) {
          hooks.selectTutorialBox();
          return;
        }
        if (tunePhase === 1) {
          hooks.nudgeTutorialBoxPitch();
          return;
        }
        if (tunePhase >= 2) {
          hooks.setTutorialBoxTimbre(PIANO_TIMBRE);
        }
      },
    },
    {
      titleKey: (): MessageKey => "tutorial.delete.title",
      bodyKey: (): MessageKey =>
        deletePhase === 0 ? "tutorial.delete.selectBody" : "tutorial.delete.body",
      hole: (): DOMRect | null => hooks.targetBoxRect(),
      watch: watchDelete(),
      perform: (): void => {
        if (deletePhase === 0) {
          hooks.selectTutorialBox();
          return;
        }
        if (deletePhase >= 1) {
          hooks.deleteTutorialBox();
        }
      },
    },
    {
      titleKey: (): MessageKey => "tutorial.features.title",
      bodyKey: (): MessageKey => (targets.radial.active() ? "tutorial.features.radial.body" : "tutorial.features.body"),
      hole: (): DOMRect | null => rectOf(targets.touchPlayBtn),
      watch: watchClick((): HTMLElement => targets.touchPlayBtn),
      perform: (): void => clickTarget(targets.touchPlayBtn),
    },
    {
      titleKey: (): MessageKey => "tutorial.advanced.title",
      bodyKey: (): MessageKey => {
        if (!targets.radial.active()) {
          return advancedPhase === 0 ? "tutorial.advanced.body" : advancedPhase === 1 ? "tutorial.advanced.body2" : "tutorial.advanced.body3";
        }
        if (radialAdvancedReached || targets.featuresNavBtn.offsetParent !== null) {
          return "tutorial.advanced.radial.features";
        }
        if (!targets.radial.isOpen()) {
          return "tutorial.advanced.radial.open";
        }
        if (targets.radial.itemRect("panel:Scene Settings") !== null) {
          return "tutorial.advanced.radial.target";
        }
        return targets.radial.itemRect("scene") !== null ? "tutorial.advanced.radial.scene" : "tutorial.advanced.radial.back";
      },
      hole: (): DOMRect | null => {
        if (!targets.radial.active()) {
          return advancedPhase === 0 ? rectOf(targets.scenePanelBtn) : advancedPhase === 1 ? textRectOf(targets.sceneSettingsNavBtn) : textRectOf(targets.featuresNavBtn);
        }
        return targets.featuresNavBtn.offsetParent !== null ? textRectOf(targets.featuresNavBtn) : radialScenePathHole("panel:Scene Settings");
      },
      watch: (advance: () => void): (() => void) =>
        targets.radial.active() ? watchRadialFeatures()(advance) : watchAdvanced()(advance),
      perform: (): void => {
        if (targets.radial.active()) {
          if (targets.featuresNavBtn.offsetParent !== null) {
            clickTarget(targets.featuresNavBtn);
          } else {
            performRadialStage("scene", "panel:Scene Settings");
          }
          return;
        }
        if (advancedPhase === 0) {
          clickTarget(targets.scenePanelBtn);
          return;
        }
        if (advancedPhase === 1) {
          clickTarget(targets.sceneSettingsNavBtn);
          return;
        }
        clickTarget(targets.featuresNavBtn);
      },
    },
    {
      titleKey: (): MessageKey => "tutorial.save.title",
      bodyKey: (): MessageKey => {
        if (!(targets.radial.active() && targets.communityNavBtn() !== null)) {
          return savePhase === 0 ? "tutorial.save.body" : "tutorial.save.body2";
        }
        if (!targets.radial.isOpen()) {
          return "tutorial.save.radial.open";
        }
        if (targets.radial.itemRect("panel:Community") !== null) {
          return "tutorial.save.radial.target";
        }
        return targets.radial.itemRect("scene") !== null ? "tutorial.save.radial.scene" : "tutorial.save.radial.back";
      },
      hole: (): DOMRect | null => {
        if (targets.radial.active() && targets.communityNavBtn() !== null) {
          return radialScenePathHole("panel:Community");
        }
        if (savePhase === 0) {
          return rectOf(targets.scenePanelBtn);
        }
        const community = targets.communityNavBtn();
        return community !== null ? textRectOf(community) : rectOf(targets.scenePanelBtn);
      },
      watch: (advance: () => void): (() => void) => {
        if (targets.radial.active()) {
          return targets.communityNavBtn() !== null ? watchFeedOpen()(advance) : watchRadialScenePanelOpened()(advance);
        }
        return watchSave()(advance);
      },
      perform: (): void => {
        const community = targets.communityNavBtn();
        if (targets.radial.active()) {
          if (community !== null) {
            performRadialStage("scene", "panel:Community");
          } else {
            targets.radial.openScenePanelSection("Scene Settings");
          }
          return;
        }
        if (savePhase === 0) {
          clickTarget(targets.scenePanelBtn);
          return;
        }
        if (community !== null) {
          clickTarget(community);
          return;
        }
        clickTarget(targets.scenePanelBtn);
      },
    },
  ];

  const HOLE_PAD = 8;
  const HOLE_RADIUS = 7;

  /** Full-screen rectangle with a rounded-rectangle hole punched out (evenodd), so only the target element stays interactive. */
  function holePath(vw: number, vh: number, x: number, y: number, w: number, h: number): string {
    const rr = Math.min(HOLE_RADIUS, w / 2, h / 2);
    return (
      `M0 0 H${vw} V${vh} H0 Z ` +
      `M${x + rr} ${y} H${x + w - rr} Q${x + w} ${y} ${x + w} ${y + rr} ` +
      `V${y + h - rr} Q${x + w} ${y + h} ${x + w - rr} ${y + h} ` +
      `H${x + rr} Q${x} ${y + h} ${x} ${y + h - rr} ` +
      `V${y + rr} Q${x} ${y} ${x + rr} ${y} Z`
    );
  }

  /**
   * Places the caption beside the hole on whichever side has the most free space
   * (below / above / right / left), so it never lands on top of the spotlight.
   * Each candidate is pinned outside the hole on its axis and centred on the
   * other, then the side with the largest leftover slack that actually fits is
   * chosen — falling back to the roomiest side when none fully fits.
   */
  function placeCaption(x: number, y: number, w: number, h: number, vw: number, vh: number): void {
    const capW = caption.offsetWidth;
    const capH = caption.offsetHeight;
    const gap = 16;
    const m = 12;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const clampX = (l: number): number => clamp(l, m, Math.max(m, vw - capW - m));
    const clampY = (t: number): number => clamp(t, m, Math.max(m, vh - capH - m));
    const candidates = [
      { left: clampX(cx - capW / 2), top: y + h + gap, slack: vh - (y + h) - gap - capH },
      { left: clampX(cx - capW / 2), top: y - gap - capH, slack: y - gap - capH },
      { left: x + w + gap, top: clampY(cy - capH / 2), slack: vw - (x + w) - gap - capW },
      { left: x - gap - capW, top: clampY(cy - capH / 2), slack: x - gap - capW },
    ];
    const fitting = candidates.filter((c: { slack: number }): boolean => c.slack >= 0);
    const pool = fitting.length > 0 ? fitting : candidates;
    pool.sort((a: { slack: number }, b: { slack: number }): number => b.slack - a.slack);
    const chosen = pool[0];
    caption.style.left = `${clampX(chosen.left)}px`;
    caption.style.top = `${clampY(chosen.top)}px`;
  }

  function layout(): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    maskSvg.setAttribute("viewBox", `0 0 ${vw} ${vh}`);
    const rect = steps[index].hole();
    if (rect === null) {
      maskPath.setAttribute("d", `M0 0 H${vw} V${vh} H0 Z`);
      ring.style.display = "none";
      caption.style.left = `${(vw - caption.offsetWidth) / 2}px`;
      caption.style.top = `${(vh - caption.offsetHeight) / 2}px`;
      return;
    }
    const x = rect.left - HOLE_PAD;
    const y = rect.top - HOLE_PAD;
    const w = rect.width + HOLE_PAD * 2;
    const h = rect.height + HOLE_PAD * 2;
    maskPath.setAttribute("d", holePath(vw, vh, x, y, w, h));
    ring.style.display = "";
    ring.setAttribute("x", String(x));
    ring.setAttribute("y", String(y));
    ring.setAttribute("width", String(w));
    ring.setAttribute("height", String(h));
    ring.setAttribute("rx", String(HOLE_RADIUS));
    placeCaption(x, y, w, h, vw, vh);
  }

  function detachWatch(): void {
    if (watchCleanup !== null) {
      watchCleanup();
      watchCleanup = null;
    }
  }

  /** Renders a caption body, turning `**span**` segments into <b> nodes so dictionary strings can emphasize a phrase without HTML. */
  function setCaptionBody(text: string): void {
    captionBody.textContent = "";
    const parts = text.split("**");
    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 1) {
        const bold = document.createElement("b");
        bold.textContent = parts[i];
        captionBody.appendChild(bold);
      } else if (parts[i] !== "") {
        captionBody.appendChild(document.createTextNode(parts[i]));
      }
    }
  }

  function repaint(): void {
    const bodyKey = steps[index].bodyKey();
    const captionKey = `${index}:${bodyKey}`;
    if (captionKey !== lastCaptionKey) {
      lastCaptionKey = captionKey;
      const soundWait = bodyKey === "tutorial.box.radial.contact" || bodyKey === "tutorial.box.selectBody";
      hideCaptionBriefly(soundWait ? SOUND_WAIT_PANEL_HIDE_MS : STAGE_PANEL_HIDE_MS);
    }
    const showDoneOpt = bodyKey === "tutorial.save.body2" || bodyKey === "tutorial.save.radial.target";
    captionTitle.textContent = t(steps[index].titleKey());
    setCaptionBody(t(bodyKey));
    doneOpt.style.display = showDoneOpt ? "" : "none";
    doneOptText.textContent = t("tutorial.skipConfirm.dontShow");
    progressEl.textContent = t("tutorial.progress", { current: index + 1, total: steps.length });
    skipBtn.textContent = t("tutorial.skip");
    nextBtn.textContent = t("tutorial.next");
    layout();
  }

  const STAGE_PANEL_HIDE_MS = 300;
  const SOUND_WAIT_PANEL_HIDE_MS = 1000;
  let captionRevealId = 0;
  let lastCaptionKey: string | null = null;

  /** Hides the caption panel while a stage's layout settles, so it never flashes at a stale position. A stage is one 次へ press / one guided tap; repaint detects the change via the step-index + bodyKey pair. The box post-placement (sound-wait) captions hide longer so the first contact sound rings before the caption returns. */
  function hideCaptionBriefly(hideMs: number): void {
    caption.style.visibility = "hidden";
    if (captionRevealId !== 0) {
      window.clearTimeout(captionRevealId);
    }
    captionRevealId = window.setTimeout((): void => {
      captionRevealId = 0;
      caption.style.visibility = "";
      repaint();
    }, hideMs);
  }

  function render(): void {
    overlay.classList.remove("tutorial-hidden");
    hooks.setDeleteLock(steps[index].titleKey() !== "tutorial.delete.title");
    if (targets.radial.active()) {
      hooks.ensurePlacementOn();
    }
    repaint();
    detachWatch();
    watchCleanup = steps[index].watch(advance);
  }

  function advance(): void {
    if (index >= steps.length - 1) {
      finish({ completed: true, dontShowAgain: doneOptInput.checked });
      return;
    }
    index += 1;
    render();
  }

  function finish(result: Partial<TutorialState>): void {
    if (!shown) {
      return;
    }
    shown = false;
    track(result.skipped === true ? "tutorial_skip" : "tutorial_done");
    detachWatch();
    hooks.setDeleteLock(false);
    window.removeEventListener("resize", layout);
    window.clearInterval(pollId);
    if (captionRevealId !== 0) {
      window.clearTimeout(captionRevealId);
      captionRevealId = 0;
    }
    caption.style.visibility = "";
    overlay.remove();
    hooks.onEnd();
    const prev = loadState() ?? { completed: false, skipped: false, dontShowAgain: false };
    saveState({ ...prev, ...result });
  }

  /**
   * Skipping asks for confirmation rather than quitting outright. The tutorial
   * overlay is hidden while the modal is up because the modal sits below it in
   * the stacking order; cancelling restores the overlay, confirming ends the
   * tutorial and (when the checkbox is ticked) suppresses future auto-shows.
   */
  function confirmSkip(): void {
    overlay.style.display = "none";
    showActionModal<"end" | "continue">(
      t("tutorial.skipConfirm.title"),
      t("tutorial.skipConfirm.message"),
      [
        { label: t("tutorial.skipConfirm.cancel"), value: "continue", subtle: true },
        { label: t("tutorial.skipConfirm.end"), value: "end" },
      ],
      (value: "end" | "continue", checked: boolean): void => {
        overlay.style.display = "";
        if (value === "end") {
          finish({ skipped: true, dontShowAgain: checked });
        }
      },
      { checkbox: { label: t("tutorial.skipConfirm.dontShow"), checked: loadState()?.dontShowAgain ?? false } },
    );
  }

  skipBtn.addEventListener("click", confirmSkip);
  nextBtn.addEventListener("click", (): void => {
    steps[index].perform();
  });

  function start(): void {
    if (shown) {
      return;
    }
    shown = true;
    index = 0;
    doneOptInput.checked = loadState()?.dontShowAgain === true;
    lastCaptionKey = null;
    boxPhase = 0;
    boxRadialPlaced = false;
    advancedPhase = 0;
    radialAdvancedReached = false;
    tunePhase = 0;
    deletePhase = 0;
    savePhase = 0;
    hooks.onBegin();
    document.body.appendChild(overlay);
    window.addEventListener("resize", layout);
    pollId = window.setInterval((): void => {
      if (targets.radial.active()) {
        hooks.ensurePlacementOn();
      }
      repaint();
    }, 250);
    render();
  }

  return {
    start,
    shouldAutoShow: (): boolean => {
      const state = loadState();
      return state === null || !(state.completed || state.dontShowAgain);
    },
  };
}
